import React from 'react';

import { Typography } from '@mui/material';

import {
  Loss,
  NewsType,
  ProvinceEvent,
  SpellType,
} from '../../@types/KingdomNews';
import { useIntelState } from '../../context/IntelState';

const ProvinceNewsReport: React.FC = () => {
  const { intelState } = useIntelState();
  const provNews = intelState.provinceNews;

  const renderSpellDuration = (name: string, spellType: SpellType) => {
    const count = getNewsTypeEventCount(NewsType.sorcery, spellType);
    return (
      !!count && (
        <Typography variant='body2' gutterBottom component='div'>
          <span>{name} </span>
          <span>{count}</span>
          <span> times, for a total of </span>
          <span style={{ color: 'red' }}>
            {getEventSpellDuration(NewsType.sorcery, spellType)}
          </span>
          <span> days</span>
        </Typography>
      )
    );
  };

  const renderLosses = () => {
    if (!provNews) return null;
    const result: JSX.Element[] = [];
    for (const key in provNews?.groupedLosses) {
      const value = provNews.groupedLosses[key];
      result.push(
        <Typography variant='body2' gutterBottom component='div' key={key}>
          <span style={{ textTransform: 'capitalize' }}>{key}</span>: {value}
        </Typography>
      );
    }
    return result;
  };

  const getNewsTypeEventCount = (newsType: NewsType, spellType?: SpellType) => {
    if (spellType) {
      return provNews?.events.filter(
        (x) => x.newsType === newsType && x.spellType === spellType
      ).length;
    } else {
      return provNews?.events.filter((x) => x.newsType === newsType).length;
    }
  };

  const getNewsTypeAttackerEventCount = (
    newsType: NewsType,
    spellType?: SpellType
  ) => {
    if (spellType) {
      return provNews?.events.filter(
        (x) => x.newsType === newsType && x.spellType === spellType
      ).length;
    } else {
      return provNews?.events.filter(
        (x) => x.newsType === newsType && !!x.attacker
      ).length;
    }
  };

  const getEventLossAmount = (newsType: NewsType, spellType?: SpellType) => {
    if (!provNews) return 0;
    const filtered: ProvinceEvent[] = spellType
      ? provNews.events.filter(
          (x) => x.newsType === newsType && x.spellType === spellType
        )
      : provNews.events.filter((x) => x.newsType === newsType);
    const sum = filtered.reduce(
      (t: number, item: ProvinceEvent) =>
        t + item.losses.reduce((t2: number, l: Loss) => t2 + l.amount, 0),
      0
    );
    return sum;
  };

  const getEventSpellDuration = (newsType: NewsType, spellType: SpellType) => {
    if (!provNews) return 0;
    const filtered: ProvinceEvent[] = provNews.events.filter(
      (x) => x.newsType === newsType && x.spellType === spellType
    );
    const sum = filtered.reduce(
      (t: number, item: ProvinceEvent) => t + item.spellDuration,
      0
    );
    return sum;
  };

  const getMeteorsSummary = () => {
    if (!provNews) return 0;
    const strikes = provNews.meteorStrikes;
    const result: JSX.Element[] = [];
    for (const key in strikes) {
      const value = strikes[key];
      result.push(
        <div key={key}>
          <span style={{ color: 'red' }}>{value}&nbsp;</span>
          <span>{key}&nbsp;</span>
        </div>
      );
    }
    return result;
  };

  const getPropSummary = () => {
    if (!provNews) return 0;
    const strikes = provNews.propAttacks;
    const result: JSX.Element[] = [];
    for (const key in strikes) {
      const value = strikes[key];
      result.push(
        <div key={key} style={{ display: 'inline-block' }}>
          <span style={{ color: 'red' }}>{value}&nbsp;</span>
          <span>{key}&nbsp;</span>
        </div>
      );
    }
    return result;
  };

  if (!provNews) return null;

  const fbsCount = getNewsTypeEventCount(NewsType.fireballStrike);
  const meteorCount = getNewsTypeEventCount(NewsType.meteorStrike);
  const propCount = getNewsTypeEventCount(NewsType.propaganda);
  const landLustCount = getNewsTypeEventCount(NewsType.landLust);
  const bribeGenCount = getNewsTypeEventCount(NewsType.bribeGenerals);
  return (
    <>
      <Typography variant='h3' gutterBottom component='div'>
        Province News Report
      </Typography>
      <Typography variant='h5' gutterBottom component='div'>
        {provNews.startDate.fullDateString} - {provNews.endDate.fullDateString}{' '}
        ({provNews.numberOfDays} days)
      </Typography>

      <Typography
        variant='body1'
        gutterBottom
        component='div'
        sx={{ marginTop: 2 }}
      >
        Total Losses
      </Typography>
      {renderLosses()}

      {!!(fbsCount || meteorCount || propCount || landLustCount) && (
        <Typography
          variant='body1'
          gutterBottom
          component='div'
          sx={{ marginTop: 2 }}
        >
          Fireballs, Meteors, Propaganda, Landlust
        </Typography>
      )}
      {!!fbsCount && (
        <Typography variant='body2' gutterBottom component='div'>
          <span>Fireballs hit </span>
          <span>{fbsCount}</span>
          <span> times, losing </span>
          <span style={{ color: 'red' }}>
            {getEventLossAmount(NewsType.fireballStrike)}
          </span>
          <span> peasants</span>
        </Typography>
      )}
      {!!meteorCount && (
        <Typography variant='body2' gutterBottom component='div'>
          <span>Meteors hit </span>
          <span>{meteorCount}</span>
          <span> times, losing </span>
          {getMeteorsSummary()}
        </Typography>
      )}
      {!!propCount && (
        <Typography variant='body2' gutterBottom component='div'>
          <span>Propaganda was spread </span>
          <span>{propCount}</span>
          <span> times, losing </span>
          {getPropSummary()}
        </Typography>
      )}
      {!!landLustCount && (
        <Typography variant='body2' gutterBottom component='div'>
          <span>Landlust </span>
          <span>{landLustCount}</span>
          <span> times, losing </span>
          <span style={{ color: 'red' }}>
            {getEventLossAmount(NewsType.landLust)}
          </span>
          <span> acres</span>
        </Typography>
      )}

      <Typography
        variant='body1'
        gutterBottom
        component='div'
        sx={{ marginTop: 2 }}
      >
        Enemy Ops
      </Typography>

      {renderSpellDuration('Greed', SpellType.greed)}
      {renderSpellDuration('Meteors', SpellType.meteors)}
      {renderSpellDuration('Pitfalls', SpellType.pitfalls)}
      {renderSpellDuration('Blizzards', SpellType.blizzards)}

      {!!bribeGenCount && (
        <Typography variant='body2' gutterBottom component='div'>
          <span>Generals were bribed </span>
          <span>{getNewsTypeEventCount(NewsType.bribeGenerals)}</span>
          <span> times.</span>
        </Typography>
      )}
      <Typography
        variant='body1'
        gutterBottom
        component='div'
        sx={{ marginTop: 2 }}
      >
        Wizards / Thieves
      </Typography>
      <Typography variant='body2' gutterBottom component='div'>
        <span>Enemy wizards were thwarted </span>
        <span>{getNewsTypeAttackerEventCount(NewsType.sorcery)}</span>
        <span> times. The offenders were: {provNews.enemyMages}</span>
      </Typography>
      <Typography variant='body2' gutterBottom component='div'>
        <span>Enemy thieves were identified, caught and executed </span>
        <span>{getNewsTypeAttackerEventCount(NewsType.thieves)}</span>
        <span> times. The offenders were: {provNews.enemyThieves}</span>
      </Typography>

      <Typography
        variant='body1'
        gutterBottom
        component='div'
        sx={{ marginTop: 2 }}
      >
        Support Spells
      </Typography>

      {renderSpellDuration('Illuminate Shadows', SpellType.illuminate)}
      {renderSpellDuration('Protection', SpellType.protection)}
    </>
  );
};

export default ProvinceNewsReport;
